import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { openModal } from "react-url-modal";
import { ITournament } from "@/redux/tournaments/types";
import { getTokenFromStorage } from "@/utils";
import { Button, Toast } from "@/components";
import { isTournamentPast } from "@/utils/tournaments/tournamentHelpers";
import { formatDateTimeRussian } from "@/utils/date/dateGrouping";
import styles from "./TournamentsItem.module.scss";
import shareIcon from "@/assets/icons/share.svg";

interface TournamentsItemProps {
  item: ITournament;
  button?: boolean;
  isMyGames?: boolean;
  isMyTournaments?: boolean;
  isCreateTournaments?: boolean;
  isAppliedTournament?: boolean;
  handleCancelModalVisible?: (id: number) => void;
  setIsRequiredAuthOpened: (value: boolean) => void;
  modalWindow?: boolean;
  isOrganizator?: boolean;
  onDeleteClick?: () => void;
}

export const TournamentsItem: React.FC<TournamentsItemProps> = ({
  item,
  button = true,
  isMyGames = false,
  isMyTournaments = false,
  isCreateTournaments = false,
  isAppliedTournament = false,
  handleCancelModalVisible,
  setIsRequiredAuthOpened,
  modalWindow = false,
  isOrganizator = false,
  onDeleteClick,
}) => {
  const navigate = useNavigate();
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  
  const isPast = isTournamentPast(item);

  const formatDateTime = (
    date: string,
    startTime: string,
    endTime: string
  ) => {
    return formatDateTimeRussian(date, startTime, endTime);
  };

  const formatLevelValue = (level: number) => {
    return Number(level).toFixed(1).replace('.', ',');
  };

  const formatLevel = () => {
    if (!item.level_to) {
      return `${formatLevelValue(item.level_from)}+`;
    }
    if (item.level_from === item.level_to) {
      return `${formatLevelValue(item.level_from)}`;
    }
    return `${formatLevelValue(item.level_from)} - ${formatLevelValue(item.level_to)}`;
  };

  const handleJoinTournament = () => {
    const token = getTokenFromStorage();
    if (!token) {
      setIsRequiredAuthOpened(true);
      return;
    }

    if (isCreateTournaments) {
      openModal({
        name: "joinTournament",
        params: {
          idTournament: String(item.id),
          organizator: "true",
        },
      });
    } else if (isMyGames || isAppliedTournament) {
      openModal({
        name: "joinTournament",
        params: {
          idTournament: String(item.id),
          isApplied: "true",
        },
      });
    } else {
      openModal({
        name: "joinTournament",
        params: {
          idTournament: String(item.id),
        },
      });
    }
  };

  const handleEditTournament = () => {
    const token = getTokenFromStorage();
    if (!token) {
      setIsRequiredAuthOpened(true);
      return;
    }

    openModal({
      name: "editTournament",
      params: {
        tournamentId: String(item.id),
      },
    });
  };

  const createDeepLink = (tournamentId: number): string => {
    return `${window.location.origin}/padel/tournaments/${tournamentId}`;
  };

  const handleShareTournament = async () => {
    const shareLink = createDeepLink(item.id);
    const shareText = `Турнир: ${item.tournament_type}, ${formatDateTime(item.date, item.start_time, item.end_time)}`;

    try {
      if (navigator.share) {
        await navigator.share({
          title: "Турнир по паделю",
          text: shareText,
          url: shareLink,
        });
        return;
      }
      // Fallback: копируем ссылку в буфер обмена
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(shareLink);
        setToastMessage("Ссылка скопирована в буфер обмена");
        setShowToast(true);
      }
    } catch (error: any) {
      // Пользователь отменил шаринг или произошла ошибка
      if (error.name !== "AbortError") {
        console.error("Share error:", error);
        if (navigator.clipboard && navigator.clipboard.writeText) {
          try {
            await navigator.clipboard.writeText(shareLink);
            setToastMessage("Ссылка скопирована в буфер обмена");
            setShowToast(true);
          } catch (clipError) {
            console.error("Clipboard error:", clipError);
          }
        }
      }
    }
  };

  const handleCopyPhone = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(item.organizer_phone);
        setToastMessage("Телефон скопирован в буфер обмена");
        setShowToast(true);
      } else {
        // Fallback для старых браузеров
        const textArea = document.createElement("textarea");
        textArea.value = item.organizer_phone;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
        setToastMessage("Телефон скопирован в буфер обмена");
        setShowToast(true);
      }
    } catch (error) {
      console.error("Failed to copy phone:", error);
    }
  };

  const renderActions = () => {
    if (isMyGames || isAppliedTournament) {
      return (
        <div className={styles.actions}>
          {handleCancelModalVisible && (
            <Button
              onClickFunc={() => handleCancelModalVisible(item.id)}
              newStyles={styles.buttonTransparent}
            >
              Отменить заявку
            </Button>
          )}
        </div>
      );
    }

    if (isCreateTournaments) {
      if (isPast) {
        return null;
      }
      return (
        <div className={styles.actions}>
          <Button onClickFunc={handleEditTournament} newStyles={styles.buttonTransparent}>
            Редактировать турнир
          </Button>
          {handleCancelModalVisible && (
            <Button
              onClickFunc={() => handleCancelModalVisible(item.id)}
              newStyles={styles.deleteButton}
            >
              Удалить турнир
            </Button>
          )}
        </div>
      );
    }

    // Скрываем кнопку "Записаться" для прошедших турниров
    if (isPast) {
      return null;
    }
    return (
      <div className={styles.actions}>
        <Button onClickFunc={handleJoinTournament} newStyles={styles.button}>
          Записаться
        </Button>
      </div>
    );
  };

  return (
    <div className={`${styles.item} ${modalWindow ? styles.modalWindow : ''}`}>
      <div className={styles.info}>
        <div className={styles.sectionRow}>
          <div className={styles.sectionTitle}>Тип турнира</div>
          <div className={styles.pill}>
            <span className={styles.pillText}>{item.tournament_type}</span>
          </div>
        </div>

        <div className={styles.sectionRow}>
          <div className={styles.sectionTitle}>Время</div>
          <div className={styles.pill}>
            <span className={styles.pillText}>
              {formatDateTime(item.date, item.start_time, item.end_time)}
            </span>
          </div>
        </div>

        <div className={styles.sectionRow}>
          <div className={styles.sectionTitle}>Уровень</div>
          <div className={styles.pill}>
            <span className={styles.pillText}>{formatLevel()}</span>
          </div>
        </div>

        <div className={styles.sectionRow}>
          <div className={styles.sectionTitle}>Взнос с человека</div>
          <div className={styles.pill}>
            <span className={styles.pillText}>{item.entry_fee}</span>
          </div>
        </div>

        <div className={styles.sectionRow}>
          <div className={styles.sectionTitle}>Участники</div>
          <div 
            className={`${styles.pill} ${styles.pillClickable}`}
            onClick={(e) => {
              e.stopPropagation();
              console.log("Opening tournament applications modal for tournament:", item.id);
              openModal({
                name: "tournamentApplications",
                params: {
                  tournamentId: String(item.id),
                },
              });
            }}
            style={{ cursor: "pointer" }}
          >
            <span className={styles.pillText}>
              {item.applications?.length || 0} / {item.max_players || 16}
            </span>
          </div>
        </div>

        <div className={styles.sectionRow}>
          <div className={styles.sectionTitle}>Организатор</div>
          <div className={styles.pillOrganizer}>
            <span className={styles.pillText}>
              {item.organizer_name}
            </span>
            <span 
              className={styles.phone}
              onClick={handleCopyPhone}
              style={{ cursor: "pointer" }}
            >
              {item.organizer_phone.replace(/(\d{1})(\d{3})(\d{3})(\d{2})(\d{2})/, "7 ($2) $3-$4-$5")}
            </span>
          </div>
        </div>

        <div className={styles.commentRow}>
          <div className={styles.sectionTitle}>Описание</div>
          {item.description && (
            <>
              <div
                className={`${styles.comment} ${isDescriptionExpanded ? styles.commentExpanded : ""}`}
              >
                {item.description}
              </div>
              {item.description.length > 100 && (
                <button
                  className={styles.expandButton}
                  onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
                >
                  {isDescriptionExpanded ? "Свернуть" : "Развернуть"}
                </button>
              )}
            </>
          )}
        </div>

        {isCreateTournaments && (
          <div className={styles.detailInfo}>
            <div className={styles.detailLabel}>
              <span className={styles.sectionTitle}>Заявки</span>
            </div>
            <div className={styles.pill}>
              <span className={styles.pillText}>
                {item.applications?.length || 0}
              </span>
            </div>
          </div>
        )}
      </div>
      <div className={styles.footer}>
        <Button onClickFunc={handleShareTournament} newStyles={styles.shareButton}>
          Поделиться
          <img src={shareIcon} alt="share" className={styles.shareIcon} />
        </Button>
        {isOrganizator && modalWindow && (
          <div className={styles.organizerActions}>
            <Button
              onClickFunc={handleEditTournament}
              newStyles={styles.editButton}
            >
              Редактировать турнир
            </Button>
            <Button
              onClickFunc={onDeleteClick || (() => {})}
              newStyles={styles.deleteButton}
            >
              Удалить турнир
            </Button>
          </div>
        )}
        {button && renderActions()}
      </div>
      <Toast
        message={toastMessage}
        isVisible={showToast}
        onClose={() => setShowToast(false)}
      />
    </div>
  );
};
